import {
  CloudDownload as DownloadIcon,
  NoteAdd as NoteAddIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useAuthContext } from "../../auth/AuthContext";
import { useDownloadCallRecording } from "./useDownloadCallRecording";

export interface CallRecordingRow {
  readonly recId: string;
  readonly contactName: string;
  readonly agentName: string;
  readonly date: string;
  readonly startTime: string;
  readonly duration: string;
  readonly isPlayable: boolean;
}

interface Props {
  readonly rows: CallRecordingRow[];
  readonly playingRecId: string | null;
  readonly loadingRecId: string | null;
  readonly generatingNoteRecId: string | null;
  readonly onPlayToggle: (recId: string) => void;
  readonly onCreateNoteDraft: (recId: string) => void;
}

export const CallRecordingsList: FC<Props> = ({
  rows,
  playingRecId,
  loadingRecId,
  generatingNoteRecId,
  onPlayToggle,
  onCreateNoteDraft,
}) => {
  if (rows.length === 0) {
    return <Typography>Brak rozmów w wybranym okresie</Typography>;
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Kontakt</TableCell>
            <TableCell>Pracownik</TableCell>
            <TableCell align="right">Czas trwania</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <CallRecordingRowItem
              key={row.recId}
              row={row}
              isPlaying={playingRecId === row.recId}
              isLoading={loadingRecId === row.recId}
              isCreatingNote={generatingNoteRecId === row.recId}
              onPlayToggle={onPlayToggle}
              onCreateNoteDraft={onCreateNoteDraft}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface RowProps {
  readonly row: CallRecordingRow;
  readonly isPlaying: boolean;
  readonly isLoading: boolean;
  readonly isCreatingNote: boolean;
  readonly onPlayToggle: (recId: string) => void;
  readonly onCreateNoteDraft: (recId: string) => void;
}

const CallRecordingRowItem: FC<RowProps> = ({
  row,
  isPlaying,
  isLoading,
  isCreatingNote,
  onPlayToggle,
  onCreateNoteDraft,
}) => {
  const { download, isLoading: isDownloading } = useDownloadCallRecording(row.recId, row.contactName, row.date);
  const { context } = useAuthContext();
  const isAdmin = context?.role === "ADMIN";
  const noRecording = !row.isPlayable;
  const playbackDisabled = noRecording || !isAdmin;
  const playbackDisabledReason = !isAdmin
    ? "Tylko administrator może odsłuchiwać i pobierać nagrania"
    : "Brak nagrania";

  return (
    <TableRow hover>
      <TableCell>{row.date}</TableCell>
      <TableCell>{row.contactName}</TableCell>
      <TableCell>{row.agentName}</TableCell>
      <TableCell align="right">{row.duration}</TableCell>
      <TableCell align="right">
        <Tooltip title={playbackDisabled ? playbackDisabledReason : playButtonLabel(isPlaying)}>
          <span>
            <IconButton
              aria-label={playButtonLabel(isPlaying)}
              disabled={playbackDisabled || isLoading}
              size="small"
              onClick={() => onPlayToggle(row.recId)}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={playbackDisabled ? playbackDisabledReason : "Pobierz"}>
          <span>
            <IconButton
              aria-label="Pobierz"
              disabled={playbackDisabled || isDownloading}
              size="small"
              onClick={() => download()}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={noRecording ? "Brak nagrania" : "Stwórz notatkę"}>
          <span>
            <IconButton
              aria-label="Stwórz notatkę"
              disabled={noRecording || isCreatingNote}
              size="small"
              onClick={() => onCreateNoteDraft(row.recId)}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

function playButtonLabel(isPlaying: boolean): string {
  return isPlaying ? "Wstrzymaj" : "Odtwórz";
}

export default CallRecordingsList;
